import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search,
  X,
  Loader2,
  FileCode,
  ChevronDown,
  ChevronRight,
  CaseSensitive,
  Regex,
  Filter,
  AlertCircle,
} from 'lucide-react';
import { useSearchStore } from '@/store/searchStore';
import { useEditorStore } from '@/store/editorStore';
import * as fileService from '@/services/file.service';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Search Panel — workspace-wide text search                         */
/* ================================================================== */

interface SearchPanelProps {
  workspaceId: string;
}

/* ---- File icon by extension ---- */
function getFileIcon(path: string): string {
  if (path.endsWith('.sol')) return '◆';
  if (path.endsWith('.ts') || path.endsWith('.tsx')) return '⊤';
  if (path.endsWith('.js') || path.endsWith('.jsx')) return '⟐';
  if (path.endsWith('.json')) return '{}';
  if (path.endsWith('.md')) return '▤';
  if (path.endsWith('.toml')) return '⚙';
  if (path.endsWith('.yaml') || path.endsWith('.yml')) return '⚙';
  return '▫';
}

/* ---- Highlight matched text ---- */
function HighlightedText({ text, query, caseSensitive }: { text: string; query: string; caseSensitive: boolean }) {
  if (!query) return <span>{text}</span>;

  try {
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, flags);
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-500/30 text-yellow-200 rounded-sm px-px">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  } catch {
    return <span>{text}</span>;
  }
}

/* ---- File group component ---- */
function FileGroup({
  path,
  matches,
  query,
  caseSensitive,
  onMatchClick,
}: {
  path: string;
  matches: { line: number; text: string }[];
  query: string;
  caseSensitive: boolean;
  onMatchClick: (path: string, line: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const fileName = path.split('/').pop() ?? path;

  return (
    <div className="mb-1">
      {/* File header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-1.5 px-2 py-1 hover:bg-[#2a2a2a] rounded transition-colors group"
      >
        {collapsed ? (
          <ChevronRight className="w-3 h-3 text-[#666] shrink-0" />
        ) : (
          <ChevronDown className="w-3 h-3 text-[#666] shrink-0" />
        )}
        <span className="text-xs shrink-0">{getFileIcon(path)}</span>
        <span className="text-xs text-[#e8e8e8] truncate flex-1 text-left font-medium">
          {fileName}
        </span>
        <span className="text-[9px] text-[#666] shrink-0">{path}</span>
        <span className="text-[9px] bg-[#3c3c3c] text-[#999] px-1 rounded shrink-0">
          {matches.length}
        </span>
      </button>

      {/* Matches */}
      {!collapsed && (
        <div className="ml-5 border-l border-[#333]">
          {matches.map((match, i) => (
            <button
              key={i}
              onClick={() => onMatchClick(path, match.line)}
              className="w-full flex items-start gap-2 px-2 py-0.5 hover:bg-[#2a2d2e] transition-colors text-left"
            >
              <span className="text-[9px] text-[#555] font-mono w-6 text-right shrink-0 pt-px">
                {match.line}
              </span>
              <span className="text-[11px] text-[#bbb] font-mono truncate">
                <HighlightedText text={match.text.trim()} query={query} caseSensitive={caseSensitive} />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Search Panel                                                      */
/* ================================================================== */

export default function SearchPanel({ workspaceId }: SearchPanelProps) {
  const {
    query,
    groupedResults,
    totalMatches,
    truncated,
    isSearching,
    caseSensitive,
    useRegex,
    includePattern,
    error,
    setQuery,
    setCaseSensitive,
    setUseRegex,
    setIncludePattern,
    search,
    clear,
  } = useSearchStore();

  const { openFile } = useEditorStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search
  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (value.trim().length >= 2) {
          search(workspaceId);
        }
      }, 400);
    },
    [setQuery, search, workspaceId],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        search(workspaceId);
      }
      if (e.key === 'Escape') {
        clear();
      }
    },
    [search, clear, workspaceId],
  );

  const handleMatchClick = useCallback(
    async (path: string, _line: number) => {
      try {
        const content = await fileService.getFileContent(workspaceId, path);
        openFile(path, content);
      } catch {
        // If file fetch fails, still try to open
        openFile(path, '');
      }
    },
    [workspaceId, openFile],
  );

  const fileCount = groupedResults.size;

  return (
    <div className="h-full flex flex-col bg-[#252526] text-gray-300">
      {/* Search header */}
      <div className="px-3 py-2 border-b border-[#3c3c3c]">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
          <Search className="w-3.5 h-3.5 text-blue-400" />
          Search
        </span>
      </div>

      {/* Search input */}
      <div className="px-2 pt-2 pb-1 space-y-1.5">
        <div className="flex items-center gap-1 bg-[#3c3c3c] rounded border border-[#555] focus-within:border-blue-500 transition-colors">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search files..."
            className="flex-1 bg-transparent text-xs text-[#d4d4d4] px-2 py-1.5 outline-none placeholder-[#666]"
          />

          {/* Toggle buttons */}
          <button
            onClick={() => { setCaseSensitive(!caseSensitive); if (query.trim().length >= 2) search(workspaceId); }}
            className={cn(
              'p-1 rounded transition-colors',
              caseSensitive ? 'bg-blue-600/30 text-blue-400' : 'text-[#666] hover:text-[#999]',
            )}
            title="Match Case"
          >
            <CaseSensitive className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setUseRegex(!useRegex); if (query.trim().length >= 2) search(workspaceId); }}
            className={cn(
              'p-1 rounded transition-colors',
              useRegex ? 'bg-blue-600/30 text-blue-400' : 'text-[#666] hover:text-[#999]',
            )}
            title="Use Regex"
          >
            <Regex className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'p-1 rounded transition-colors mr-0.5',
              showFilters ? 'bg-blue-600/30 text-blue-400' : 'text-[#666] hover:text-[#999]',
            )}
            title="Toggle Filters"
          >
            <Filter className="w-3.5 h-3.5" />
          </button>

          {query && (
            <button
              onClick={() => { clear(); inputRef.current?.focus(); }}
              className="p-1 text-[#666] hover:text-white transition-colors mr-0.5"
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* File filter */}
        {showFilters && (
          <div className="flex items-center gap-1 bg-[#3c3c3c] rounded border border-[#444] px-2 py-1">
            <FileCode className="w-3 h-3 text-[#666] shrink-0" />
            <input
              type="text"
              value={includePattern}
              onChange={(e) => setIncludePattern(e.target.value)}
              placeholder="Files to include (e.g. *.sol)"
              className="flex-1 bg-transparent text-[10px] text-[#d4d4d4] outline-none placeholder-[#555]"
            />
          </div>
        )}
      </div>

      {/* Status line */}
      {(totalMatches > 0 || isSearching) && (
        <div className="px-3 py-1 text-[10px] text-[#666] flex items-center gap-1.5 border-b border-[#333]">
          {isSearching ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <span>{totalMatches} result{totalMatches !== 1 ? 's' : ''} in {fileCount} file{fileCount !== 1 ? 's' : ''}</span>
              {truncated && <span className="text-yellow-500">(truncated)</span>}
            </>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-2 my-1 flex items-start gap-1.5 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded">
          <AlertCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
          <span className="text-[10px] text-red-400">{error}</span>
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-1 py-1">
        {!isSearching && query && totalMatches === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-8 text-[#555]">
            <Search className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-xs">No results found</p>
            <p className="text-[10px] mt-1">Try a different search term</p>
          </div>
        )}

        {!query && (
          <div className="flex flex-col items-center justify-center py-8 text-[#555]">
            <Search className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-xs">Search across workspace files</p>
            <p className="text-[10px] mt-1 text-[#444]">Type at least 2 characters to begin</p>
          </div>
        )}

        {Array.from(groupedResults.entries()).map(([path, matches]) => (
          <FileGroup
            key={path}
            path={path}
            matches={matches}
            query={query}
            caseSensitive={caseSensitive}
            onMatchClick={handleMatchClick}
          />
        ))}
      </div>
    </div>
  );
}
